import React, { useEffect, useRef, useState } from 'react';
import { fabric } from 'fabric';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Switch } from '@/components/ui/switch';

import { FONT_PAIRS, loadFont } from '../fonts';
import { TEMPLATES } from '../templates';
import type { ArtboardSize, BrandInfo, FontPair, Translate } from '../types';
import type { DesignEditorApi } from '../useDesignEditor';
import { SectionLabel } from '../ui';

const THUMB_W = 280;

/**
 * Render every template to a small picture with the vendor's own artboard,
 * brand and pictures, so the grid previews what they will actually get.
 * Sequential: thirteen layouts each loading fonts and images in parallel would
 * stall the editor while it opens.
 */
function useTemplateThumbs(
    enabled: boolean,
    art: ArtboardSize,
    brand: BrandInfo,
    pair: FontPair,
    useBrandFont: boolean,
    images: string[],
    t: Translate,
): Record<string, string> {
    const [thumbs, setThumbs] = useState<Record<string, string>>({});
    const key = `${art.w}x${art.h}|${pair.id}|${useBrandFont}|${brand.ink}|${brand.ground}|${brand.accent}|${brand.logoUrl}|${brand.font}|${images.join(',')}`;
    const cache = useRef<Map<string, Record<string, string>>>(new Map());
    useEffect(() => {
        if (!enabled) return undefined;
        const hit = cache.current.get(key);
        if (hit) { setThumbs(hit); return undefined; }
        let cancelled = false;
        const out: Record<string, string> = {};
        setThumbs({});
        (async () => {
            const scale = THUMB_W / art.w;
            const th = Math.round(art.h * scale);
            for (const def of TEMPLATES) {
                if (cancelled) return;
                try {
                    const res = await def.build({ art, brand, pair, t, images, useBrandFont });
                    if (cancelled) return;
                    const sc = new fabric.StaticCanvas(null as any, { width: THUMB_W, height: th, enableRetinaScaling: false, renderOnAddRemove: false } as any);
                    sc.backgroundColor = res.background;
                    sc.setViewportTransform([scale, 0, 0, scale, 0, 0]);
                    res.objects.forEach((o) => sc.add(o));
                    sc.renderAll();
                    out[def.id] = sc.toDataURL({ format: 'jpeg', quality: 0.86 } as any);
                    sc.dispose();
                    setThumbs({ ...out });
                } catch {
                    /* a thumbnail that fails is shown as a blank tile */
                }
            }
            cache.current.set(key, { ...out });
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, key]);
    return thumbs;
}

export const TemplatesPanel: React.FC<{
    ed: DesignEditorApi;
    brand: BrandInfo;
    pair: FontPair;
    onPairChange: (p: FontPair) => void;
    useBrandFont: boolean;
    onUseBrandFontChange: (v: boolean) => void;
    tr: Translate;
}> = ({ ed, brand, pair, onPairChange, useBrandFont, onUseBrandFontChange, tr }) => {
    const { t } = useTranslation();
    const [busy, setBusy] = useState<string | null>(null);
    const images = ed.ready ? ed.templateImages() : [];
    const thumbs = useTemplateThumbs(ed.ready, ed.artboard, brand, pair, useBrandFont, images, tr);

    useEffect(() => {
        FONT_PAIRS.forEach((p) => { void loadFont(p.heading, p.headingWeight); });
    }, []);

    const apply = async (id: string) => {
        setBusy(id);
        try { await ed.applyTemplate(id, { pair, useBrandFont }); } finally { setBusy(null); }
    };

    const aspect = ed.artboard.h / ed.artboard.w;

    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
                <SectionLabel>{t('DESIGN_EDITOR.TYPE_PAIRING')}</SectionLabel>
                <div className="grid grid-cols-4 gap-1.5">
                    {FONT_PAIRS.map((p) => (
                        <button
                            key={p.id}
                            type="button"
                            onClick={() => onPairChange(p)}
                            title={`${p.heading} + ${p.body}`}
                            className={cn(
                                'flex h-11 cursor-pointer items-center justify-center rounded-lg border bg-background text-[19px] leading-none text-foreground transition-colors hover:border-foreground/40',
                                pair.id === p.id && 'border-foreground ring-[0.5px] ring-foreground',
                            )}
                            style={{ fontFamily: `"${p.heading}", serif`, fontWeight: p.headingWeight }}
                        >
                            {p.headingUpper ? 'AA' : 'Aa'}
                        </button>
                    ))}
                </div>
                <div className="text-[11px] text-muted-foreground">{pair.heading} · {pair.body}</div>
                {brand.font && (
                    <label className="flex cursor-pointer items-center justify-between gap-2 text-xs text-foreground">
                        <span>{t('DESIGN_EDITOR.USE_BRAND_FONT', { font: brand.font })}</span>
                        <Switch checked={useBrandFont} onCheckedChange={onUseBrandFontChange} />
                    </label>
                )}
            </div>

            <div className="flex flex-col gap-2">
                <SectionLabel>{t('DESIGN_EDITOR.TEMPLATES')}</SectionLabel>
                <p className="text-[11px] leading-snug text-muted-foreground">{t('DESIGN_EDITOR.TEMPLATES_HINT')}</p>
                <div className="grid grid-cols-2 gap-2.5">
                    {TEMPLATES.map((def) => (
                        <button
                            key={def.id}
                            type="button"
                            data-template={def.id}
                            onClick={() => { void apply(def.id); }}
                            disabled={!!busy}
                            className="group flex cursor-pointer flex-col gap-1.5 text-start disabled:cursor-wait"
                        >
                            <div
                                className="relative w-full overflow-hidden rounded-lg bg-muted ring-1 ring-border transition-shadow group-hover:ring-foreground/50"
                                style={{ aspectRatio: `${1} / ${Math.min(2, Math.max(0.4, aspect))}` }}
                            >
                                {thumbs[def.id] ? (
                                    <img src={thumbs[def.id]} alt="" className="absolute inset-0 size-full object-cover" />
                                ) : (
                                    <div className="absolute inset-0 animate-pulse bg-foreground/[0.04]" />
                                )}
                                {busy === def.id && (
                                    <div className="absolute inset-0 flex items-center justify-center bg-background/60">
                                        <Loader2 className="size-4 animate-spin text-foreground" />
                                    </div>
                                )}
                            </div>
                            <span className="truncate text-[11.5px] text-foreground">{t(def.nameKey)}</span>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
};
